import { Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { clearHighlightMarker, dropHighlightMarker } from '../map/highlightMarker'
import { useMap } from '../routes/MapView'
import type { Result } from './mapResults'
import { readRecents, writeRecent, clearRecents, type Recent } from './recents'
import { useSearch, type SearchBias } from './useSearch'
import { SearchDropdown } from './SearchDropdown'
import styles from './SearchBar.module.css'

/**
 * SearchBar — UI-SPEC §Components 1 (lines 211-239).
 *
 * Wires `useSearch` to the live MapLibre map (`useMap()`):
 *   - Bias: `{ lat, lon, radius: 50000, countrySet: 'RO' }` from `map.getCenter()`
 *     snapshotted ON FOCUS (sticky-per-focus session — UI-SPEC hint plus plan
 *     Task 5 rationale: avoids re-running `useSearch` on every map pan).
 *   - Fallback when map not ready: Bucharest center per D-60 / D-72.
 *   - onSelect: `map.flyTo({ center: [lng, lat], zoom: 16, speed: 1.2 })`,
 *     `dropHighlightMarker(map, [lng, lat], theme)`, `writeRecent(...)`,
 *     clear input, blur, close dropdown.
 *   - Escape: close dropdown + `clearHighlightMarker(map)`.
 */

const BUCHAREST_BIAS: SearchBias = {
  lat: 44.4268,
  lon: 26.1025,
  radius: 50000,
  countrySet: 'RO',
}

export function SearchBar() {
  const { map, theme } = useMap()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const [recents, setRecents] = useState<Recent[]>(() => readRecents())

  // Bias refreshed on focus (sticky-per-focus). Initialised to Bucharest for
  // the first render (map may not be ready yet — D-60 / D-72 fallback).
  const [bias, setBias] = useState<SearchBias>(BUCHAREST_BIAS)

  const state = useSearch(query, { bias })

  // Dropdown visibility — open while focused OR when there is any input text.
  const dropdownOpen = focused || query.length > 0

  const effectiveBias: SearchBias = useMemo(() => bias, [bias])

  function refreshBiasFromMap(): void {
    if (!map) {
      setBias(BUCHAREST_BIAS)
      return
    }
    const c = map.getCenter()
    setBias({
      lat: c.lat,
      lon: c.lng,
      radius: 50000,
      countrySet: 'RO',
    })
  }

  function handleFocus(): void {
    refreshBiasFromMap()
    setFocused(true)
  }

  function handleBlur(): void {
    // Defer blur close so a click on a dropdown row registers first.
    window.setTimeout(() => setFocused(false), 120)
  }

  function handleSelect(r: Result | Recent): void {
    if (!map) return
    const { lat, lng, id, label, sublabel } = r
    map.flyTo({ center: [lng, lat], zoom: 16, speed: 1.2 })
    dropHighlightMarker(map, [lng, lat], theme)
    writeRecent({
      id,
      label,
      sublabel,
      lat,
      lng,
      savedAt: Date.now(),
    })
    setRecents(readRecents())
    setQuery('')
    setFocused(false)
    inputRef.current?.blur()
  }

  function handleClear(): void {
    setQuery('')
    inputRef.current?.focus()
  }

  function handleClearRecents(): void {
    clearRecents()
    setRecents([])
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      if (query.length > 0) {
        setQuery('')
      } else {
        setFocused(false)
        inputRef.current?.blur()
      }
      if (map) clearHighlightMarker(map)
    }
  }

  // Keep recents in sync when the component first mounts with fresh storage
  // (in case another surface wrote to localStorage before SearchBar mounted).
  useEffect(() => {
    setRecents(readRecents())
  }, [])

  const barClasses = [
    styles.bar,
    focused ? styles.focused : '',
    dropdownOpen ? styles.open : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.container}>
      <div
        className={styles.combobox}
        role="combobox"
        aria-expanded={dropdownOpen}
        aria-haspopup="listbox"
        aria-owns="search-dropdown"
        aria-controls="search-dropdown"
      >
        <div className={barClasses}>
          <Search
            size={20}
            strokeWidth={2}
            aria-hidden="true"
            className={styles.searchIcon}
          />
          <input
            ref={inputRef}
            type="search"
            className={styles.input}
            aria-label="Search places in Romania"
            placeholder="Search for a place in Romania"
            value={query}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
          {query.length > 0 && (
            <button
              type="button"
              className={styles.clearButton}
              aria-label="Clear search"
              onMouseDown={(e) => e.preventDefault() /* keep input focus */}
              onClick={handleClear}
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </div>

        {dropdownOpen && (
          <SearchDropdown
            state={state}
            recents={recents}
            onSelectResult={handleSelect}
            onSelectRecent={handleSelect}
            onClearRecents={handleClearRecents}
          />
        )}
      </div>
      {/* bias is referenced to satisfy TS unused-var guards if future refactors
          drop the memo; keep this read to document intent. */}
      <span style={{ display: 'none' }} aria-hidden="true">
        {effectiveBias.countrySet}
      </span>
    </div>
  )
}
